import { ScanLine, FileSearch, Activity, SlidersHorizontal } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/primitives";

const DIAG_POINTS = [
  { icon: ScanLine, label: "System Scan" },
  { icon: FileSearch, label: "Fault Analysis" },
  { icon: Activity, label: "Live Data" },
  { icon: SlidersHorizontal, label: "Service Functions" },
];

const SYSTEMS = [
  { slug: "engine", name: "Engine", img: "/images/mech-engine.webp", desc: "Complete diagnostics, repair & performance care." },
  { slug: "brakes", name: "Brakes", img: "/images/mech-brakes.webp", desc: "Discs, calipers, pads & braking precision." },
  { slug: "suspension", name: "Suspension", img: "/images/mech-suspension.webp", desc: "Dampers, arms & ride comfort restoration." },
  { slug: "transmission", name: "Transmission", img: "/images/mech-transmission.webp", desc: "Gearbox service, mechatronics & clutch care." },
  { slug: "electrical", name: "Electrical", img: "/images/mech-electrical.webp", desc: "Wiring, ECU & advanced electronic systems." },
];

export default function Expertise() {
  return (
    <>
      <section
        id="diagnostics"
        data-testid="diagnostics-section"
        className="bg-graphite py-20 text-ivory sm:py-28"
      >
        <div className="mx-auto grid max-w-7xl gap-12 px-6 sm:px-10 md:grid-cols-2 md:items-center">
          <Reveal className="order-2 md:order-1">
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src="/images/diagnostics.webp"
                alt="Technician using a diagnostic tablet at V-Elite Drives Tirupur"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="absolute inset-0 rounded-2xl border border-gold/20" />
            </div>
          </Reveal>
          <div className="order-1 md:order-2">
            <Reveal>
              <Eyebrow index="05">
                Advanced Diagnostics
              </Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-4xl">
                State-of-the-Art <span className="gold-text">Diagnostics</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-ivory/60 sm:text-base">
                Precision starts with knowing exactly what's wrong.
              </p>
            </Reveal>
            <Reveal delay={0.3} className="mt-8 grid grid-cols-2 gap-4">
              {DIAG_POINTS.map((p) => (
                <div
                  key={p.label}
                  data-testid={`diag-point-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-center gap-3 rounded-xl border border-gold/25 bg-white/5 px-4 py-4"
                >
                  <p.icon size={18} strokeWidth={1.5} className="shrink-0 text-gold" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-ivory/80">
                    {p.label}
                  </span>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      <section
        id="expertise"
        data-testid="expertise-section"
        className="bg-espresso py-20 sm:py-28"
      >
        <div className="mx-auto max-w-7xl px-6 sm:px-10">
          <Reveal>
            <Eyebrow index="06">Mechanical Expertise</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-ivory sm:text-4xl lg:text-5xl">
              Engineering <span className="gold-text">Deserves Expertise.</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2} className="mt-12">
          <div className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto px-6 pb-4 sm:px-10">
            {SYSTEMS.map((s, i) => (
              <div
                key={s.slug}
                data-testid={`mechanical-card-${s.slug}`}
                className="group w-[70vw] shrink-0 snap-center sm:w-[320px]"
              >
                <div className="relative overflow-hidden rounded-2xl border border-gold/15">
                  <img
                    src={s.img}
                    alt={`${s.name} service at V-Elite Drives`}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/10 to-transparent" />
                  <span className="absolute right-5 top-5 font-display text-[10px] font-bold tracking-[0.3em] text-gold/80">
                    0{i + 1}
                  </span>
                  <div className="absolute inset-x-5 bottom-5">
                    <div className="font-display text-lg font-bold uppercase tracking-[0.2em] text-ivory">
                      {s.name}
                    </div>
                    <p className="mt-2 text-xs font-light leading-relaxed text-ivory/60">
                      {s.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>
    </>
  );
}
